/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import React from 'react';
import { useAppSelector } from '@/hooks';
import { useTranslations } from '@/hooks/useTranslations';
import { Card, FlexBox, Text } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { createStyles } from '@/utils/styles';

function isValidUrl(url: string): boolean {
    try {
        return Boolean(new URL(url));
    } catch {
        return false;
    }
}

const LearnMore: React.FC = () => {
    const { t } = useTranslations();
    const { currentStep } = useAppSelector(state => state.productList);
    const isVideo: boolean = !!currentStep?.learnMoreContent && isValidUrl(currentStep?.learnMoreContent);

    const learnMoreTextContent = (
        <Text data-testid="learnMore-text" style={{ fontSize: ThemingParameters.sapFontSize }}>
            {t(currentStep?.learnMoreContent)}
        </Text>
    );

    const learnMoreVideoContent = currentStep.learnMoreContent ? (
        <iframe
            title="Learn More Video"
            src={currentStep.learnMoreContent}
            width="100%"
            height="100%"
            sandbox="allow-scripts allow-same-origin allow-presentation"
            allowFullScreen
        />
    ) : null;

    return (
        <Card>
            <FlexBox direction="Column" alignItems="Start" style={styles.flexbox}>
                <Text style={styles.title}>{t('LEARN_MORE')}</Text>
                {isVideo ? learnMoreVideoContent : learnMoreTextContent}
            </FlexBox>
        </Card>
    );
};

const styles = createStyles({
    flexbox: {
        gap: ThemingParameters.sapContent_Margin_Medium,
        margin: ThemingParameters.sapContent_Margin_Small,
        marginTop: ThemingParameters.sapContent_Margin_Medium,
        color: ThemingParameters.sapTextColor,
    },
    title: {
        fontWeight: 'bold',
        color: ThemingParameters.sapList_TableGroupHeaderTextColor,
        fontSize: ThemingParameters.sapFontHeader5Size,
        paddingTop: ThemingParameters.sapContent_Margin_Small,
    },
});
export default LearnMore;
